import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { superadminAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Trash2, LogOut, Loader2, Store, Users, ShoppingCart, Search, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function SuperAdminDashboard() {
  const navigate = useNavigate();
  const { user, logout, isAuthenticated } = useAuthStore();
  const [establishments, setEstablishments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [form, setForm] = useState({
    name: '', description: '', address: '', admin_email: '', admin_name: ''
  });

  useEffect(() => {
    if (!isAuthenticated() || user?.role !== 'superadmin') {
      navigate('/superadmin/login');
    }
  }, [isAuthenticated, user, navigate]);

  const loadData = async () => {
    try {
      const res = await superadminAPI.getEstablishments();
      setEstablishments(res.data);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await superadminAPI.createEstablishment(form);
      toast.success(`${form.name} cree ! Un email a ete envoye a ${form.admin_email}`);
      setDialogOpen(false);
      setForm({ name: '', description: '', address: '', admin_email: '', admin_name: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await superadminAPI.deleteEstablishment(deleteTarget.id);
      toast.success(`${deleteTarget.name} supprime`);
      setDeleteTarget(null);
      loadData();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/superadmin/login');
  };

  const filtered = establishments.filter(e =>
    !searchQuery ||
    e.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    e.admin?.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="admin-mobile-header bg-[#111827] text-white px-4 sm:px-6 py-4 flex items-center justify-between sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Building2 className="w-7 h-7 text-[#7C3AED]" />
          <div>
            <h1 className="text-lg font-bold" style={{ fontFamily: 'Manrope' }}>GlassOrder</h1>
            <p className="text-xs text-gray-400">Super Admin</p>
          </div>
        </div>
        <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white hover:bg-white/10" data-testid="superadmin-logout-btn">
          <LogOut className="w-5 h-5" />
        </Button>
      </header>

      <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Store className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>{establishments.length}</p>
                <p className="text-xs text-gray-500">Etablissements</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {establishments.reduce((s, e) => s + (e.staff_count || 0), 0)}
                </p>
                <p className="text-xs text-gray-500">Staff total</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                  {establishments.reduce((s, e) => s + (e.order_count || 0), 0)}
                </p>
                <p className="text-xs text-gray-500">Commandes totales</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Establishments */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle style={{ fontFamily: 'Manrope' }}>Etablissements</CardTitle>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-establishment-btn">
                    <Plus className="w-4 h-4 mr-2" />Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Nouvel etablissement</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreate} className="space-y-4">
                    <div>
                      <Label htmlFor="est-name">Nom de l'etablissement</Label>
                      <Input id="est-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} required placeholder="Le Sunset Bar" data-testid="est-name-input" />
                    </div>
                    <div>
                      <Label htmlFor="est-desc">Description</Label>
                      <Input id="est-desc" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Bar en bord de mer" data-testid="est-description-input" />
                    </div>
                    <div>
                      <Label htmlFor="est-addr">Adresse</Label>
                      <Input id="est-addr" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} placeholder="123 Rue Example" data-testid="est-address-input" />
                    </div>
                    <div className="border-t pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">Administrateur du restaurant</p>
                      <div className="space-y-3">
                        <div>
                          <Label htmlFor="admin-name">Nom</Label>
                          <Input id="admin-name" value={form.admin_name} onChange={(e) => setForm({...form, admin_name: e.target.value})} required placeholder="Jean Dupont" data-testid="est-admin-name-input" />
                        </div>
                        <div>
                          <Label htmlFor="admin-email">Email</Label>
                          <Input id="admin-email" type="email" value={form.admin_email} onChange={(e) => setForm({...form, admin_email: e.target.value})} required placeholder="admin@restaurant.fr" data-testid="est-admin-email-input" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-400">Un email sera envoye a l'admin pour creer son mot de passe.</p>
                    <Button type="submit" className="w-full" disabled={saving} data-testid="est-submit-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                      Creer l'etablissement
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            <div className="relative mt-2">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input placeholder="Rechercher..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9" data-testid="est-search-input" />
            </div>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <p className="text-center text-gray-400 py-8">Aucun etablissement</p>
            ) : (
              <div className="space-y-3">
                {filtered.map((est) => (
                  <div key={est.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl" data-testid={`establishment-${est.id}`}>
                    <div className="w-12 h-12 bg-[#111827] rounded-xl flex items-center justify-center flex-shrink-0">
                      <Store className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{est.name}</h3>
                      <p className="text-sm text-gray-500 truncate">{est.address || 'Pas d\'adresse'}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {est.admin && <Badge variant="outline" className="text-xs">{est.admin.email}</Badge>}
                        <span className="text-xs text-gray-400">{est.staff_count || 0} staff</span>
                        <span className="text-xs text-gray-400">{est.order_count || 0} commandes</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="icon" className="text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteTarget(est)} data-testid={`delete-est-${est.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">
            Voulez-vous vraiment supprimer <strong>{deleteTarget?.name}</strong> et toutes ses donnees (menu, tables, commandes, staff) ?
          </p>
          <p className="text-xs text-red-500 font-medium">Cette action est irreversible.</p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} data-testid="cancel-delete-btn">Annuler</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
